import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Input, Button, message } from "antd";
import {
  Container,
  Heading,
  VStack,
  useColorModeValue,
  Select,
} from "@chakra-ui/react";
import ParticipantTable from "../components/ParticipantTable";
import Cookies from "js-cookie";
import { firebaseAPI } from "../firebaseConfig";
import { BeatLoader } from "react-spinners";
import { css } from "@emotion/react";
import axiosInstance from "../utils/axiosInstance";

const override = css`
  display: block;
  margin: 0 auto;
  border-color: red;
  //
`;
const ParticipantPage = () => {
  const { formName, programName } = useParams();

  const [participants, setParticipants] = useState([]);

  const [gradYearFilter, setGradYearFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");

  const [genderFilter, setGenderFilter] = useState("");
  const [disabilityFilter, setDisabilityFilter] = useState("");

  const [sortDirection, setSortDirection] = useState("asc");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchParticipants();
  }, []);

  const fetchAndDownloadParticipantsCSV = () => {
    if (sortedParticipants.length === 0) {
      message.warning("No participants to export.");
      return;
    }

    const headers = [
      "Name",
      "Email",
      "Graduation Year",
      "Gender",
      "Disability",
      "Status",
    ];

    const rows = sortedParticipants.map((p) => {
      const programEntry = p.participantPrograms.find(
        (program) => program.programName === programName
      );
      const profileStatus = programEntry ? programEntry.profileStatus : "";

      return [
        p.user?.name || "",
        p.user?.email || "",
        p.gradYear || "",
        p.gender || "",
        p.hasDisability ? "Yes" : "No",
        profileStatus,
      ];
    });

    const csvContent = [headers, ...rows]
      .map((row) => row.map((val) => `"${val}"`).join(","))
      .join("\n");

    const csvBlob = new Blob([csvContent], { type: "text/csv" });
    const downloadLink = document.createElement("a");
    downloadLink.href = window.URL.createObjectURL(csvBlob);
    downloadLink.download = "filtered_participants.csv";
    downloadLink.click();
  };

  const fetchParticipants = async () => {
    try {
      setLoading(true);
      let token = Cookies.get("token") || "";
      const response = await axiosInstance.get(
        `${firebaseAPI}/users/${programName}/${formName}/participants`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setParticipants(response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      message.error("No Participants found.");
      console.error("Error:", error);
    }
  };

  const handleGradYearFilterChange = (event) => {
    setGradYearFilter(event.target.value);
  };

  const handleStatusChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleNameFilterChange = (event) => {
    setNameFilter(event.target.value);
  };

  const handleEmailFilterChange = (event) => {
    setEmailFilter(event.target.value);
  };

  const handleGenderChange = (event) => {
    setGenderFilter(event.target.value);
  };

  const handleDisabilityChange = (event) => {
    setDisabilityFilter(event.target.value);
  };

  const toggleSortDirection = () => {
    setSortDirection((prevDirection) =>
      prevDirection === "asc" ? "desc" : "asc"
    );
  };

  const filteredParticipants = participants.filter((participant) => {
    const programEntry = participant.participantPrograms.find(
      (program) => program.programName === programName
    );

    const profileStatus = programEntry ? programEntry.profileStatus : "";

    const matchesGradYear =
      !gradYearFilter || participant.gradYear === parseInt(gradYearFilter);
    const matchesStatus = !statusFilter || profileStatus === statusFilter;
    const matchesName =
      !nameFilter ||
      (participant.user?.name &&
        participant.user.name.toLowerCase().includes(nameFilter.toLowerCase()));
    const matchesEmail =
      !emailFilter ||
      (participant.user?.email &&
        participant.user.email
          .toLowerCase()
          .includes(emailFilter.toLowerCase()));

    const matchesGender =
      !genderFilter ||
      (participant.gender &&
        participant.gender.toLowerCase() === genderFilter.toLowerCase());

    // ✅ Corrected disability filter using `hasDisability` boolean
    const matchesDisability =
      !disabilityFilter ||
      (disabilityFilter === "true" && participant.hasDisability === true) ||
      (disabilityFilter === "false" && participant.hasDisability === false);

    return (
      matchesGradYear &&
      matchesStatus &&
      matchesName &&
      matchesEmail &&
      matchesGender &&
      matchesDisability
    );
  });

  const sortedParticipants = filteredParticipants.sort((a, b) => {
    const gradYearA = a.gradYear ?? 0;
    const gradYearB = b.gradYear ?? 0;
    const sortValue = sortDirection === "asc" ? 1 : -1;
    return sortValue * (gradYearA - gradYearB);
  });

  return (
    <Container
      style={{ background: "#F7F7F7" }}
      maxW="container.lg"
      mt="6"
      bg="#F7F7F7"
      height="80%"
    >
      {loading && (
        <BeatLoader
          color="#123abc"
          loading={loading}
          css={override}
          size={15}
        />
      )}
      <Heading as="h1" mb="4">
        Participants Page
      </Heading>
      <Button onClick={fetchAndDownloadParticipantsCSV}>Export</Button>

      <VStack spacing="4" mb="4">
        <Input
          placeholder="Filter by Graduation Year"
          value={gradYearFilter}
          onChange={handleGradYearFilterChange}
          size="lg"
          style={{ width: "50%" }}
        />
        <Input
          placeholder="Filter by Name"
          value={nameFilter}
          onChange={handleNameFilterChange}
          size="lg"
          style={{ width: "50%" }}
        />
        <Input
          placeholder="Filter by Email"
          value={emailFilter}
          onChange={handleEmailFilterChange}
          size="lg"
          style={{ width: "50%" }}
        />

        <Select
          placeholder="Select status"
          value={statusFilter}
          onChange={handleStatusChange}
          style={{ fontSize: "14px", padding: "4px" }}
          colorScheme="blue"
        >
          <option value="pending">Pending</option>
          <option value="qualified">Qualified</option>
          <option value="disqualified">Disqualified</option>
        </Select>

        <Select
          placeholder="Select Gender"
          value={genderFilter}
          onChange={handleGenderChange}
          style={{ fontSize: "14px", padding: "4px" }}
        >
          <option value="male">Male</option>
          <option value="female">Female</option>
        </Select>

        <Select
          placeholder="Select Disability Status"
          value={disabilityFilter}
          onChange={handleDisabilityChange}
          style={{ fontSize: "14px", padding: "4px" }}
        >
          <option value="true">Has Disability</option>
          <option value="false">No Disability</option>
        </Select>
      </VStack>

      <div>
        <p style={{ marginBottom: "10px", fontWeight: "bold" }}>
          Showing {sortedParticipants.length} participant
          {sortedParticipants.length !== 1 ? "s" : ""}
        </p>
        <ParticipantTable
          participants={sortedParticipants}
          programName={programName}
          fetchParticipants={fetchParticipants}
        />
      </div>
    </Container>
  );
};

export default ParticipantPage;
